/* Reading a scanned SMDD ticket QR.

   The payload is `v1.<nonce>.<ciphertext>.<tag>`, all lowercase hex: AES-GCM
   with a 12-byte nonce and a 16-byte tag, wrapping a protobuf `User`:

     message User {
       string company      = 1;
       string name         = 2;
       string position     = 3;
       string ticketNumber = 4;
     }

   AES-256, with the key derived as SHA-256 of the configured secret. Confirmed
   against 60 live tickets before the scheme was documented: ciphertext length
   matched the protobuf encoding exactly every time, which it would not if the
   shape were anything else. GCM is a stream mode, so there is no padding.

   This is what makes scanning work at all. The service re-encrypts with a fresh
   nonce on every read, so the string on a guest's e-ticket never matches the one
   in our roster — but decrypting it yields the ticket number regardless of which
   nonce was used. */

const HEX = /^[0-9a-f]+$/i;

/* The secret is a passphrase, not key material: the service does
   `key = SHA-256(secret)` and uses that as the AES-256 key. Hashing here rather
   than expecting 32 raw bytes is the difference between the real secret working
   and being rejected as the wrong length. Their config requires 16 chars
   minimum (TICKET_QR_ENCRYPTION_SECRET). */
export const MIN_SECRET_LENGTH = 16;

export class QrKeyError extends Error {}

export async function importKey(secret) {
  const text = (secret || '').trim();
  if (!text) throw new QrKeyError('no secret given');
  if (text.length < MIN_SECRET_LENGTH) {
    throw new QrKeyError(
      `the secret must be at least ${MIN_SECRET_LENGTH} characters — ` +
      `this one is ${text.length}`);
  }
  const digest = await crypto.subtle.digest(
    'SHA-256', new TextEncoder().encode(text));
  return crypto.subtle.importKey('raw', digest, 'AES-GCM', false, ['decrypt']);
}

const unhex = h => new Uint8Array(h.match(/../g).map(x => parseInt(x, 16)));

/** Minimal protobuf reader: four length-delimited strings, nothing else. */
function readUser(bytes) {
  const FIELDS = { 1: 'company', 2: 'name', 3: 'position', 4: 'ticketNumber' };
  const out = { company: '', name: '', position: '', ticketNumber: '' };
  const decoder = new TextDecoder();
  let i = 0;
  while (i < bytes.length) {
    // varint key: field number in the upper bits, wire type in the lower three
    let key = 0, shift = 0;
    for (;;) {
      if (i >= bytes.length) throw new Error('truncated field key');
      const b = bytes[i++];
      key |= (b & 0x7f) << shift;
      if (!(b & 0x80)) break;
      shift += 7;
    }
    const field = key >>> 3;
    const wire = key & 7;
    if (wire !== 2) throw new Error(`unexpected wire type ${wire} on field ${field}`);

    let len = 0;
    shift = 0;
    for (;;) {
      if (i >= bytes.length) throw new Error('truncated length');
      const b = bytes[i++];
      len |= (b & 0x7f) << shift;
      if (!(b & 0x80)) break;
      shift += 7;
    }
    if (i + len > bytes.length) throw new Error('field runs past the end');
    const value = decoder.decode(bytes.subarray(i, i + len));
    i += len;
    if (FIELDS[field]) out[FIELDS[field]] = value;   // unknown fields ignored
  }
  return out;
}

export const looksLikeTicketQr = s => /^v\d+\.[0-9a-f]+\.[0-9a-f]+\.[0-9a-f]+$/i
  .test((s || '').trim());

/**
 * Decrypt one scanned QR string.
 * @param {string} qr    the `v1.…` payload
 * @param {CryptoKey} key  from importKey()
 * @returns {Promise<{company,name,position,ticketNumber}>}
 */
export async function decodeQr(qr, key) {
  const parts = (qr || '').trim().split('.');
  if (parts.length !== 4) throw new Error('not a v1.nonce.ciphertext.tag payload');
  const [version, nonceHex, cipherHex, tagHex] = parts;
  if (version !== 'v1') throw new Error(`unknown payload version ${version}`);
  if (![nonceHex, cipherHex, tagHex].every(h => HEX.test(h) && h.length % 2 === 0)) {
    throw new Error('payload is not valid hex');
  }
  const nonce = unhex(nonceHex);
  const cipher = unhex(cipherHex);
  const tag = unhex(tagHex);
  if (nonce.length !== 12) throw new Error(`nonce is ${nonce.length} bytes, expected 12`);
  if (tag.length !== 16) throw new Error(`tag is ${tag.length} bytes, expected 16`);

  // WebCrypto wants the tag appended to the ciphertext, not passed separately.
  const sealed = new Uint8Array(cipher.length + tag.length);
  sealed.set(cipher);
  sealed.set(tag, cipher.length);

  let plain;
  try {
    plain = new Uint8Array(await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: nonce, tagLength: 128 }, key, sealed));
  } catch {
    // GCM refuses to say why, and that is the point: wrong key and tampered
    // payload are indistinguishable.
    throw new Error('could not decrypt — wrong key, or the QR is not ours');
  }
  return readUser(plain);
}
